<script>
import moment from 'moment'
import ReportResultsModal from '@/components/ReportResultsModal'
import ReportSave from '@/components/ReportSave'

export default {
  name: 'SavedReports',
  props: {
    shared: { type: Boolean, default: false }
  },
  components: {
    ReportResultsModal,
    ReportSave
  },
  data() {
    return {
      includeShared: false,
      reportsFilter: '',
      reports: [],
      selectedReport: {}
    }
  },
  computed: {
    reportFields() {
      let fields = [
        {
          key: 'reportName',
          sortable: true
        },
        {
          key: 'saveDatetime',
          label: 'Saved At',
          formatter: value => moment(value).format('MM/DD/YYYY hh:mm A'),
          sortable: true
        },
        {
          key: 'saveNotes',
          label: 'Notes'
        }
      ]
      if (this.includeShared) {
        fields.push({
          key: 'personId',
          label: 'Owned By',
          formatter: (value, key, item) => item.personByPersonId.fullName,
          sortable: true
        })
      }
      if (!this.shared) {
        fields.push('edit')
      }
      fields.push('open')
      fields.push('delete')
      return fields
    },
    selectedSaveData() {
      return this.selectedReport.saveData || {}
    },
    selectedReportDescription() {
      return (
        (this.selectedReport.metricsDashReportByReportName || {})
          .reportDescription || ''
      )
    },
    selectedReportDetails() {
      return (
        (this.selectedReport.metricsDashReportByReportName || {})
          .reportDetails || {}
      )
    },
    selectedReportTitle() {
      return `${this.selectedReport.reportName} [${moment(
        this.selectedReport.saveDatetime
      ).format('MM/DD/YYYY hh:mm A')}]`
    }
  },
  watch: {
    shared: function() {
      this.includeShared = this.shared
    },
    includeShared: function() {
      this.loadReports()
    }
  },
  methods: {
    loadReports() {
      this.graphql(
        /* GraphQL */ `
          query lookupOptions_ReportSavedLookup(
            $personId: Int
            $personId2: Int
          ) {
            allMetricsDashSaves(
              orderBy: [SAVE_DATETIME_DESC, REPORT_NAME_ASC]
              filter: {
                personId: { equalTo: $personId, notEqualTo: $personId2 }
              }
            ) {
              nodes {
                saveId
                personId
                reportName
                saveDatetime
                saveMetadata
                savePermissions
                saveData
                saveNotes
                metricsDashReportByReportName {
                  reportDescription
                  reportDetails
                }
                personByPersonId {
                  fullName
                }
              }
            }
          }
        `,
        {
          personId: this.includeShared ? null : this.$store.state.personId,
          personId2: this.shared ? this.$store.state.personId : null
        }
      ).then(data => {
        this.reports = data.allMetricsDashSaves.nodes
      })
    },
    loadReport(report) {
      this.selectedReport = report
      setTimeout(() => {
        this.$bvModal.show('report-results-modal')
      }, 1)
    },
    editReport(report) {
      this.selectedReport = report
      setTimeout(() => {
        this.$bvModal.show('save-results-modal')
      }, 1)
    },
    deleteReport(report) {
      this.$bvModal
        .msgBoxConfirm(
          'Are you sure you would like to delete this report? Note that this will also remove it from anyone you shared it with.',
          {
            title: 'Please Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
          }
        )
        .then(value => {
          if (value) {
            this.graphql(
              /* GraphQL */ `
                mutation deleteSaveMutation($saveId: Int!) {
                  deleteMetricsDashSaveBySaveId(input: { saveId: $saveId }) {
                    clientMutationId
                  }
                }
              `,
              {
                saveId: report.saveId
              }
            ).then(() => {
              this.loadReports()
            })
          }
        })
    }
  },
  mounted() {
    this.includeShared = this.shared
    this.loadReports()
  }
}
</script>

<template>
  <div>
    <b-row class="w-100">
      <b-col>
        <b-form-group
          label-cols-lg="3"
          label-for="reportsFilter"
          class="form-field-group"
          label-class="font-weight-bold pb-0"
          label="Filter"
        >
          <b-input-group>
            <b-form-input
              v-model="reportsFilter"
              name="reportsFilter"
              autocomplete="off"
            />
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label-cols-lg="5"
          label-for="includeShared"
          class="form-field-group"
          label-class="font-weight-bold pb-0"
          label="Include Shared Reports"
          v-if="!shared"
        >
          <b-input-group>
            <b-form-checkbox
              v-model="includeShared"
              name="includeShared"
              autocomplete="off"
            />
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>
    <hr />
    <b-table
      striped
      hover
      :fields="reportFields"
      :items="reports"
      :filter="reportsFilter"
    >
      <template v-slot:cell(open)="data">
        <a
          href="javascript:{}"
          class="item-edit-icon-wrapper"
          @click="loadReport(data.item)"
        >
          <font-awesome-icon class="item-edit-icon" icon="arrow-circle-right" />
        </a>
      </template>
      <template v-slot:cell(edit)="data">
        <template v-if="data.item.personId == $store.state.personId">
          <font-awesome-icon
            icon="edit"
            title="Edit sharing / notes"
            @click="editReport(data.item)"
          />
        </template>
      </template>
      <template v-slot:cell(delete)="data">
        <template v-if="data.item.personId == $store.state.personId">
          <a
            href="javascript:{}"
            class="item-remove-icon-wrapper"
            @click="deleteReport(data.item)"
          >
            <font-awesome-icon class="item-remove-icon" icon="times-circle" />
          </a>
        </template>
      </template>
    </b-table>
    <ReportResultsModal
      :title="selectedReportTitle"
      :reportName="selectedReport.reportName"
      :reportDescription="selectedReportDescription"
      :reportParameters="selectedSaveData.reportParameters"
      :reportFields="selectedSaveData.reportFields"
      :reportRecords="selectedSaveData.reportRecords"
      :chartTypes="selectedReportDetails.chartTypes"
      :chartType="selectedSaveData.chartType"
      :allowDownload="true"
      :allowSave="false"
      :allowFavorite="false"
      :reportDetails="selectedReportDetails"
      v-if="selectedReport.reportName"
    />
    <ReportSave
      :reportName="selectedReport.reportName"
      :reportFields="selectedSaveData.reportFields"
      :reportRecords="selectedSaveData.reportRecords"
      :reportParameters="selectedSaveData.reportParameters"
      :chartTypes="selectedReportDetails.chartTypes"
      :savedData="selectedReport"
      @report-saved="loadReports"
      v-if="selectedReport.reportName"
    />
  </div>
</template>
